// @flow
import React from 'react'
import Slider from 'rc-slider'
import Switch from '@material-ui/core/Switch'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'
import stylesFicus from '../styles'
import { sliderStyle } from '../styles/ficus_map_style'
import * as colors from '../styles/colors'
import type { AnalyzeFrameworkBarType } from '../reducers/analyze_framework_bar'
import type { SettingsBarType } from '../reducers/settings'

export type PropsType = {
    config: AnalyzeFrameworkBarType,
    setConfig: Function,
    settings: SettingsBarType,
    theme: stylesFicus,
    updateSettings: Function,
}

// Help display
type StateType = {
    rasterOpacity: boolean,
    popupOpacity: boolean,
    historyItems: boolean,
    showLegend: boolean,
    cacheLayers: boolean,
}

class Settings extends React.Component<PropsType, StateType> {
    constructor(props) {
        super(props)
        this.state = {
            rasterOpacity: false,
            popupOpacity: false,
            historyItems: false,
            showLegend: false,
            cacheLayers: false,
        }
    }

    render() {
        const overrides = {
            MuiInput: {
                underline: {
                    '&:before': {
                        backgroundColor: colors.secondaryTwo,
                    },
                    '&:after': {
                        backgroundColor: colors.secondaryTwo,
                    },
                },
            },
            MuiTypography: {
                body1: {
                    fontFamily: 'Roboto Condensed',
                },
            },
        }

        const onChangeOpacity = opacity => this.props.updateSettings({ opacity })
        const onChangePopupOpacity = popupOpacity => this.props.updateSettings({ popupOpacity })
        const onChangeHistoryCount = event => this.props.updateSettings({ historyCount: parseInt(event.target.value, 10) })

        const rootClassNames = [this.props.theme.controlBar]
        const containerStyle = { padding: '20px 30px' }
        const historyStyle = {
            width: 40,
        }

        return (
            <div className={rootClassNames.join(' ')}>
                <div className={this.props.theme.controlBarTop}>
                    <div style={containerStyle}>
                        <div style={{ display: 'inline-block', width: 'calc(100% - 15px)' }}>
                            <span style={{ marginRight: '10px' }}>Thematic Opacity:</span>
                            <span>{Math.round(this.props.settings.opacity * 100)}%</span>
                        </div>
                        <span
                            className={`fa fa-question-circle-o ${this.props.theme.helpIcon}`}
                            onClick={() => this.setState({ ...this.state, rasterOpacity: !this.state.rasterOpacity })}
                        />
                        <Slider
                            {...sliderStyle}
                            min={0}
                            max={1}
                            step={0.01}
                            value={this.props.settings.opacity}
                            onChange={onChangeOpacity}
                        />
                        {this.state.rasterOpacity && (
                            <div className={this.props.theme.help}>
                                Set the opacity of the raster layer in the map.
                            </div>
                        )}
                    </div>
                    <div style={containerStyle}>
                        <div style={{ display: 'inline-block', width: 'calc(100% - 15px)' }}>
                            <span style={{ marginRight: '10px' }}>Cartographic Opacity:</span>
                            <span>{Math.round(this.props.settings.popupOpacity * 100)}%</span>
                        </div>
                        <span
                            className={`fa fa-question-circle-o ${stylesFicus.helpIcon}`}
                            onClick={() => this.setState({ ...this.state, popupOpacity: !this.state.popupOpacity })}
                        />
                        <Slider
                            {...sliderStyle}
                            min={0}
                            max={1}
                            step={0.01}
                            value={this.props.settings.popupOpacity}
                            onChange={onChangePopupOpacity}
                        />
                        {this.state.popupOpacity && (
                            <div className={stylesFicus.help}>
                                Set the opacity of all overlay display elements (location popups, legend, etc.) in the
                                map.
                            </div>
                        )}
                    </div>
                    <div style={{ ...containerStyle }}>
                        <span style={{ marginRight: '10px' }}>History Items:</span>
                        <div style={{ display: 'inline-block', width: '150px' }}>
                            <Select
                                InputProps={overrides}
                                onChange={onChangeHistoryCount}
                                value={this.props.settings.historyCount}
                            >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={15}>15</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                            </Select>
                        </div>
                        <span
                            className={`fa fa-question-circle-o ${this.props.theme.helpIcon}`}
                            onClick={() => this.setState({ ...this.state, historyItems: !this.state.historyItems })}
                        />
                        {this.state.historyItems && (
                            <div className={this.props.theme.help}>
                                The maximum number of theme names to store in the browser history list.
                            </div>
                        )}
                    </div>
                    <div style={containerStyle}>
                        <span style={{ marginRight: '10px' }}>Show Legend:</span>
                        <div style={{ display: 'inline-block', width: '150px' }}>
                            <FormControl>
                                <Select
                                    onChange={e => this.props.updateSettings({ mapLegendPlacement: e.target.value })}
                                    value={this.props.settings.mapLegendPlacement}
                                    inputProps={{
                                        name: 'mapLegendPlacement',
                                        id: 'mapLegendPlacement',
                                    }}
                                >
                                    <MenuItem value={'lowerLeft'}>Lower Left</MenuItem>
                                    <MenuItem value={'lowerRight'}>Lower Right</MenuItem>
                                    <MenuItem value={'upperRight'}>Upper Right</MenuItem>
                                    <MenuItem value={'upperLeft'}>Upper Left</MenuItem>
                                    <MenuItem value={'none'}>Hide</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <span
                            className={`fa fa-question-circle-o ${this.props.theme.helpIcon}`}
                            onClick={() => this.setState({ ...this.state, showLegend: !this.state.showLegend })}
                        />
                        {this.state.showLegend && (
                            <div className={this.props.theme.help}>
                                Sets the placement of the legend on the map or turns it off.
                            </div>
                        )}
                    </div>
                    <div style={containerStyle}>
                        <span>Cache Layers </span>
                        <div style={{ display: 'inline-block', width: '160px' }}>
                            <Switch
                                checked={this.props.settings.animationMode}
                                color="secondary"
                                onChange={e => this.props.updateSettings({ animationMode: !this.props.settings.animationMode })}
                            />
                        </div>
                        <span
                            className={`fa fa-question-circle-o ${this.props.theme.helpIcon}`}
                            onClick={() => this.setState({ ...this.state, cacheLayers: !this.state.cacheLayers })}
                        />
                        {this.state.cacheLayers && (
                            <div className={this.props.theme.help}>
                                If true, then all the layers in the analysis group will be loaded into the map at once
                                to speed up rendering of the rasters when animating. In
                                brushing mode it is recommended to keep this off to allow for more accurate
                                synchronization.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }
}

export default Settings
